import { Modal, Form, message } from 'antd'
import {
    ProForm,
    ProFormSelect,
    ProFormText,
} from '@ant-design/pro-components'
import { useState, useEffect } from 'react'
import styles from './bind-bot.module.scss'
import { updateChannelApi } from '@/service/channel/api/updateChannel'
import { getBotListApi } from '@/service/bot/api/getBotList'
import type { BotItem } from '@/service/bot/types/getBotList'
import type { ChannelItemRes } from '@/service/channel/types/getChannelList'

interface BindBotProps {
    open: boolean
    record: ChannelItemRes | null
    onCancel: () => void
    onSuccess: () => void
}

const BindBot: React.FC<BindBotProps> = ({ open, record, onCancel, onSuccess }) => {
    const isUpdate = !!record?.robot_id
    const [form] = Form.useForm()
    const [loading, setLoading] = useState(false)
    const [botOptions, setBotOptions] = useState<{ label: string; value: number }[]>([])
    const [botList, setBotList] = useState<BotItem[]>([])

    useEffect(() => {
        if (open && record) {
            fetchBotList()
            form.setFieldsValue({
                channel_name: record.channel_name,
                robot_id: record.robot_id || undefined,
                robot_version_id: record.robot_version_id ?? 0,
            })
        }
    }, [open, record])

    const fetchBotList = async () => {
        try {
            const res = await getBotListApi({ page: 1, page_size: 999 })
            if (res.data?.items) {
                setBotList(res.data.items)
                setBotOptions(
                    res.data.items
                        .filter((bot) => bot.dm_robot?.robot_id)
                        .map((bot) => ({
                            label: bot.name,
                            value: bot.dm_robot.robot_id,
                        }))
                )
            }
        } catch {
            message.error('Failed to fetch bot list')
        }
    }

    const handleOk = async () => {
        try {
            const values = await form.validateFields()
            if (!record) return
            setLoading(true)
            const selectedBot = botList.find(
                (b) => b.dm_robot?.robot_id === values.robot_id
            )
            const res = await updateChannelApi({
                id: record.id,
                robot_id: values.robot_id,
                robot_version_id: Number(values.robot_version_id) || 0,
                robot_name: selectedBot?.name ?? '',
            })
            if (res.code === 0) {
                message.success(isUpdate ? 'Bot updated successfully' : 'Bot bind successfully')
                form.resetFields()
                onSuccess()
            } else {
                message.error(res.message || (isUpdate ? 'Failed to update bot' : 'Failed to bind bot'))
            }
        } catch {
            // validation failed
        } finally {
            setLoading(false)
        }
    }

    const handleCancel = () => {
        form.resetFields()
        onCancel()
    }

    return (
        <Modal
            title={isUpdate ? 'Update Bot' : 'Bind Bot'}
            open={open}
            onOk={handleOk}
            onCancel={handleCancel}
            confirmLoading={loading}
            okText={isUpdate ? 'Update' : 'Bind'}
            cancelText="Cancel"
            centered
            destroyOnClose
            width={700}
            className="bind-bot-modal"
        >
            <ProForm
                form={form}
                submitter={false}
                layout="vertical"
                style={{ marginTop: 16 }}
            >
                <ProFormText
                    name="channel_name"
                    label="Channel Name"
                    rules={[{ required: true, message: 'Please enter Channel Name' }]}
                    disabled
                />
                <ProFormSelect
                    name="robot_id"
                    label="Select Bot"
                    options={botOptions}
                    showSearch
                    rules={[{ required: true, message: 'Please select a Bot' }]}
                    className={styles.selectWrapper}
                    fieldProps={{
                        style: {
                            borderRadius: '10px',
                            backgroundColor: '#090A0D',
                            color: '#515766',
                            height: '40px',
                        },
                        dropdownStyle: {
                            background: '#1A1D24',
                            border: '1px solid #2A2D36',
                        },
                        allowClear: true,
                        filterOption: (input: string, option: any) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase()),
                    }}
                />
                <ProFormText
                    name="robot_version_id"
                    label="Robot Version ID"
                    rules={[{ required: true, message: 'Please enter Robot Version ID' }]}
                />
            </ProForm>
        </Modal>
    )
}

export default BindBot